// =============================================================================
// app/src/componentes/MudarVendedorDaVenda.tsx — a venda que saiu no nome
// errado, UMA, trocada ali na linha dela (06/10/2026).
//
// Pedido do dono: "um botão ao lado do valor da venda é legal, onde eu clico em
// uma box 'Mudar vendedor' e abre um popup para alterar".
//
// O CAMINHO LARGO JÁ EXISTIA (01/10/2026): filtrar por vendedor e dia e passar
// a lista inteira de uma vez — "todas as vendas do Alex de hoje". Faltava o
// estreito, que é o caso comum: uma venda, achada na lista, no nome errado.
// É a MESMA RPC (`trocar_vendedor_das_vendas`) com um id só no array — o banco
// não ganhou porta nova, e o rastro continua sendo um evento por venda com o
// antes e o depois.
//
// SÓ O DONO, e a permissão vem pronta de cima (LinhaVenda não decide, obedece).
// Quem nega é o corpo da definer: `exige_papel(array['dono'])`.
//
// POPUP, e não painel dentro da sanfona, porque foi o que o dono pediu — e
// porque a linha da venda já é comprida: escolher pessoa e digitar motivo ali
// empurraria a lista inteira para baixo no meio de uma conferência.
// =============================================================================
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { perfis, trocarVendedorDasVendas, type VendaDetalhada } from '../lib/consultas';
import { brl, dataHoraBR } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';
import Aviso from './Aviso';
import Carregando from './Carregando';

export default function MudarVendedorDaVenda({ venda, aoFechar, aoTrocar }: {
  venda: Pick<VendaDetalhada,
    'id' | 'codigo' | 'total' | 'vendedor' | 'vendedor_id' | 'vendida_em'>;
  aoFechar: () => void;
  /** Trocou: o nome NOVO, para o recado de quem está olhando a lista. */
  aoTrocar: (nome: string) => void;
}) {
  const [para, setPara] = useState('');
  const [motivo, setMotivo] = useState('');

  // Esc fecha, como em EscolherParcelas. Tela sem saída pelo teclado é tela da
  // qual se sai recarregando o sistema.
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') aoFechar(); };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [aoFechar]);

  // A equipe só é pedida quando o popup abre (ele monta aqui, no clique), e a
  // chave é a MESMA do filtro por vendedor: se o dono já filtrou, vem do cache.
  const equipe = useQuery({ queryKey: ['perfis'], queryFn: perfis, staleTime: 10 * 60_000 });
  // Só quem está ATIVO, e nunca quem já é o vendedor desta venda: o banco
  // recusa o desligado, e "passar para quem já é" não muda nada e não conta.
  const destinos = (equipe.data ?? []).filter((p) => p.ativo && p.id !== venda.vendedor_id);
  const nomeDoPara = destinos.find((p) => p.id === para)?.nome ?? '';

  const trocar = useMutation({
    mutationFn: () => trocarVendedorDasVendas({ vendas: [venda.id], vendedorId: para, motivo }),
    onSuccess: () => aoTrocar(nomeDoPara),
  });

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-ink/95 p-4"
         role="dialog" aria-modal="true" aria-label="Mudar vendedor da venda">
      <div className="mx-auto flex min-h-0 w-full max-w-lg flex-col gap-3 overflow-y-auto">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="rotulo">Mudar vendedor</p>
            {/* O código, o valor e a hora: é como o dono reconhece QUAL venda
                ele abriu, agora que a lista ficou atrás do popup. */}
            <p className="text-sm text-mute">
              <span className="font-mono">{venda.codigo}</span> ·{' '}
              <span className="font-mono tabular-nums">{brl(Number(venda.total))}</span> ·{' '}
              {dataHoraBR(venda.vendida_em)}
            </p>
          </div>
          <button type="button" className="botao-secundario shrink-0 py-1.5 text-xs"
                  onClick={aoFechar}>
            Voltar
          </button>
        </div>

        {/* O que muda e o que NÃO muda, escrito aqui porque é a primeira dúvida
            de quem troca — a mesma frase do painel da encomenda. */}
        <p className="text-xs text-dim">
          A venda passa a ser de quem você escolher: muda a meta, a base da
          comissão e o nome que sai no papel do cliente. Peças, valores, forma de
          pagamento e caixa ficam como estão.
        </p>

        {equipe.isLoading && <Carregando texto="Carregando a equipe" />}
        {equipe.error ? <Aviso tipo="erro">{mensagemDeErro(equipe.error)}</Aviso> : null}

        <div>
          <label className="rotulo" htmlFor={`mudar-vendedor-${venda.id}`}>Para</label>
          <select id={`mudar-vendedor-${venda.id}`} className="campo py-2" value={para}
                  onChange={(e) => setPara(e.target.value)}>
            <option value="">Escolha a pessoa</option>
            {destinos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
          <p className="mt-1 text-xs text-dim">
            {venda.vendedor
              ? `Hoje é de ${venda.vendedor}.`
              : 'Esta venda está sem vendedor.'}
          </p>
        </div>

        <div>
          <label className="rotulo" htmlFor={`mudar-vendedor-motivo-${venda.id}`}>
            Motivo (opcional)
          </label>
          <input id={`mudar-vendedor-motivo-${venda.id}`} type="text" className="campo py-2"
                 value={motivo} maxLength={200}
                 placeholder="Ex.: vendeu no login do colega"
                 onChange={(e) => setMotivo(e.target.value)} />
        </div>

        {trocar.error ? <Aviso tipo="erro">{mensagemDeErro(trocar.error)}</Aviso> : null}

        <div className="flex gap-2">
          <button type="button" className="botao-secundario flex-1 py-2 text-sm"
                  disabled={trocar.isPending} onClick={aoFechar}>
            Desistir
          </button>
          <button type="button" className="botao-primario flex-1 py-2 text-sm"
                  disabled={!para || trocar.isPending} onClick={() => trocar.mutate()}>
            {trocar.isPending ? 'Mudando…' : nomeDoPara ? `Passar para ${nomeDoPara}` : 'Mudar'}
          </button>
        </div>
      </div>
    </div>
  );
}
